'use client';
import { Tooltip } from '@heroui/tooltip';
import { PlanFeatureType } from '@lib/sanity/queries/modules/pricingMain/types';
import IconType from '@ui/Icon/StaticIcon/IconType';
import { StaticIcon } from '@ui/Icon/StaticIcon/StaticIcon';
import PortableText, { TextVariant } from '@ui/Text/PortableText';
import Visuals from '@ui/Visuals/Visuals';
import cn from '@utils/cn';
import { getIconVisual } from '@utils/getIconVisual';
import PlanFeatureImageVideo from './PlanFeatureImageVideo';

type Props = {
  data: PlanFeatureType;
  index: number;
  textVariant?: TextVariant;
};

const PlanFeatureVariant = ({ data, index, textVariant = TextVariant.TextLabel }: Props) => {
  if (data.variant === 'additional') {
    return (
      <PortableText
        content={data.title}
        variant={textVariant}
        className="w-full cursor-default rounded-lg font-medium"
      />
    );
  }

  if (data.variant === 'compareLink') {
    return (
      <div
        className="hidden items-center gap-2 lg:flex"
        onClick={() => {
          const comparerCollapse = document.getElementById('comparer-collapse')?.className;
          const comparerButton = document.getElementById('comparer-button');
          if (comparerCollapse === 'hide') {
            comparerButton?.click();
          }
          setTimeout(() => {
            const scrollToElement = document.getElementById('comparer');
            scrollToElement?.scrollIntoView({ behavior: 'smooth' });
          }, 1);
        }}
      >
        <StaticIcon icon={IconType.arrowDown} />
        <PortableText
          content={data.title}
          style={{ color: '#CA5936' }}
          variant={textVariant}
          className="cursor-pointer"
        />
      </div>
    );
  }

  if (!data.tooltipDescription) {
    return (
      <div key={index} className="flex items-center gap-2">
        <Visuals data={getIconVisual(data.icon)} />

        <PortableText className="cursor-default" content={data.title} variant={textVariant} />
      </div>
    );
  }

  return (
    <div key={index} className="flex items-center gap-2">
      <Visuals data={getIconVisual(data.icon)} />

      <Tooltip
        className="rounded-lg px-3 py-2"
        color="foreground"
        showArrow
        content={
          <div className={cn('flex w-[180px] flex-col gap-1')}>
            <PlanFeatureImageVideo imageVideo={data.imageVideo} />
            <PortableText
              variant={TextVariant.TextDescription}
              content={data.tooltipDescription}
              className="text-white"
            />
          </div>
        }
        closeDelay={0}
      >
        <span>
          <PortableText
            content={data.title}
            variant={textVariant}
            className={cn(
              'cursor-default underline decoration-black decoration-dashed decoration-1 underline-offset-4',
            )}
          />
        </span>
      </Tooltip>
    </div>
  );
};

export default PlanFeatureVariant;
